import { Button, Card, Group, Stack, Text, TextInput, ActionIcon } from '@mantine/core';
import { Plus, Trash2 } from 'react-feather';
import { RichTextEditor } from '../../../../../components/common/RichTextEditor/RichTextEditor';
import type { SessionAgendaLeaderPrompt } from '../../../../../services/api/sessionAgenda';

/**
 * MED-975 (E1.3d / AC-PS5) — Repeatable Leader Prompts (Facilitator Only)
 * list and per-prompt card.
 *
 * Each prompt has Intro Text (single-line plain) and Discussion Prompt
 * (rich text: bold / italic / underline / bullet list — provided by the
 * shared RichTextEditor on Tiptap). Order is creation order; no reorder
 * within a section.
 *
 * Out of scope: drag-reorder of prompts (explicitly deferred per AC-PS5).
 * Out of scope: rendering these prompts into coach views (that's e1.3h).
 */

export interface LeaderPromptsListProps {
  prompts: SessionAgendaLeaderPrompt[];
  onChange: (next: SessionAgendaLeaderPrompt[]) => void;
}

export function LeaderPromptsList({ prompts, onChange }: LeaderPromptsListProps) {
  const addPrompt = () => {
    onChange([
      ...prompts,
      {
        intro_text: null,
        discussion_prompt: null,
        sort_order: prompts.length + 1,
      },
    ]);
  };

  const updatePrompt = (index: number, patch: Partial<SessionAgendaLeaderPrompt>) => {
    onChange(prompts.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const removePrompt = (index: number) => {
    onChange(
      prompts
        .filter((_, i) => i !== index)
        // Renumber sort_order so it stays gap-free across the list.
        .map((p, i) => ({ ...p, sort_order: i + 1 })),
    );
  };

  return (
    <Stack gap="sm" data-testid="agenda-leader-prompts">
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">
          Leader Prompts (Facilitator Only)
        </Text>
        <Button
          leftSection={<Plus size={14} />}
          variant="default"
          size="xs"
          onClick={addPrompt}
          data-testid="agenda-add-leader-prompt"
        >
          Add Prompt
        </Button>
      </Group>

      {prompts.length === 0 ? (
        <Text size="xs" c="dimmed" fs="italic">
          No leader prompts yet. These are facilitator-only — not shown to participants.
        </Text>
      ) : (
        <Stack gap="md">
          {prompts.map((prompt, i) => (
            <LeaderPromptCard
              key={prompt.id ?? `new-${i}`}
              prompt={prompt}
              position={i + 1}
              total={prompts.length}
              onChange={patch => updatePrompt(i, patch)}
              onDelete={() => removePrompt(i)}
            />
          ))}
          {/* MED-1073 (E1.3i / AC-AP2, AC-AP3) — duplicate Add Prompt at the
              bottom of the list. Identical behavior to the top button. */}
          <Group justify="flex-end">
            <Button
              leftSection={<Plus size={14} />}
              variant="default"
              size="xs"
              onClick={addPrompt}
              data-testid="agenda-add-leader-prompt-bottom"
            >
              Add Prompt
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

interface LeaderPromptCardProps {
  prompt: SessionAgendaLeaderPrompt;
  position: number;
  total: number;
  onChange: (patch: Partial<SessionAgendaLeaderPrompt>) => void;
  onDelete: () => void;
}

function LeaderPromptCard({
  prompt,
  position,
  total,
  onChange,
  onDelete,
}: LeaderPromptCardProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" align="center" mb="sm">
        <Text size="sm" fw={500} c="dimmed">
          Prompt {position} of {total}
        </Text>
        <ActionIcon
          variant="subtle"
          color="dangerRed"
          onClick={onDelete}
          aria-label={`Delete prompt ${position}`}
        >
          <Trash2 size={16} />
        </ActionIcon>
      </Group>

      <Stack gap="md">
        <TextInput
          label="Intro Text"
          placeholder="Context or setup for this prompt..."
          value={prompt.intro_text ?? ''}
          onChange={e => onChange({ intro_text: e.currentTarget.value || null })}
        />
        <RichTextEditor
          label="Discussion Prompt"
          placeholder="What the facilitator says or asks..."
          value={prompt.discussion_prompt ?? ''}
          onChange={html => onChange({ discussion_prompt: html || null })}
          minHeight={120}
        />
      </Stack>
    </Card>
  );
}
